import React, { useState } from "react";
import {
  X,
  Send,
  BadgeCheck,
  FileText,
  Maximize2,
  Minimize2,
  XCircle,
  RefreshCcw,
  ClipboardCheck,
} from "lucide-react";
import Tabs from "./Tabs";
import LoadingButton from "./LoadingButton";
import LoadingSpinner from "../../../../common/LoadingSpinner";
import InspectionReportModal from "./report/InspectionReportModal";
import {
  isQaQcManager,
  isInspector,
  isProjectManager,
} from "../../../../../constants/projectRoles";

const statusLabel = (s) => (s || "-").toString();
const normRole = (r) => (r || "").toLowerCase();
const normStatus = (s) => (s || "").toLowerCase();

const isDraftLike = (s) => normStatus(s) === "draft";
const canSubmit = (status) => isDraftLike(status);

const canApprove = (status, projectRole) =>
  (status || "").toLowerCase() === "submitted" && isQaQcManager(projectRole);

const canReject = (status, projectRole) =>
  (status || "").toLowerCase() === "submitted" && isQaQcManager(projectRole);

const canReopen = (status, projectRole) => {
  const st = (status || "").toLowerCase();
  return (
    st === "rejected" &&
    (isInspector(projectRole) || isProjectManager(projectRole))
  );
};
const canDownloadReport = (status) => normStatus(status) === "approved";

const InspectionDrawer = ({
  open,
  onClose,
  tab,
  setTab,
  detail,
  openId,
  projectRole,
  projectId,
  busyOverlay,
  actionLoading,
  onSubmit,
  onApprove,
  onReject,
  onReopen,
  onAssignInspection,
  SummaryTab,
  ChecklistTab,
  FindingsTab,
}) => {
  const [reportOpen, setReportOpen] = useState(false);
  const [isMaximized, setIsMaximized] = useState(false);

  const inspectionId = detail?.id || openId;
  const inspectionNumber = detail?.inspection_number || "—";
  if (!open) return null;

  const role = normRole(projectRole);
  const status = detail?.status;

  // ROLE-BASED VISIBILITY
  const showSubmitBtn = isInspector(role) || isQaQcManager(role);
  const showApproveRejectBtn = isQaQcManager(role);
  const showReopenBtn = isInspector(role) || isQaQcManager(role);
  const showAssignInspectionBtn =
    Boolean(onAssignInspection) &&
    (isInspector(role) || isQaQcManager(role) || isProjectManager(role));

  return (
    <div className="fixed inset-0 z-50">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />

      {/* Drawer */}
      <div
        className={[
          "absolute right-0 top-0 h-full bg-white shadow-xl flex flex-col",
          isMaximized ? "w-full max-w-none" : "w-full max-w-3xl",
        ].join(" ")}
      >
        {/* Header */}
        <div className="p-4 border-b flex items-center justify-between">
          <div>
            <div className="text-sm text-gray-500">Inspection</div>
            <div className="text-lg font-semibold text-gray-900">
              {inspectionNumber} •{" "}
              {statusLabel(detail?.status)}
            </div>
          </div>

          {/* Report */}
          <button
            type="button"
            onClick={() => setReportOpen(true)}
            disabled={!projectId || !inspectionId || !canDownloadReport(status)}
            className={`inline-flex items-center gap-2 px-3 py-2 rounded-md border text-sm font-medium
    transition-colors
    ${
      canDownloadReport(status)
        ? "border-blue-600 bg-blue-600 text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
        : "border-gray-200 bg-gray-100 text-gray-400 cursor-not-allowed"
    }
  `}
            title={
              canDownloadReport(status)
                ? "Generate / Download Inspection Report"
                : "Report is available after inspection approval"
            }
          >
            <FileText className="w-4 h-4" />
            Generate Report
          </button>

          <div className="flex items-center gap-2">
            {/* Maximize / Restore */}
            <button
              type="button"
              onClick={() => setIsMaximized((v) => !v)}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-md border text-sm hover:bg-gray-50"
              title={isMaximized ? "Restore" : "Maximize"}
            >
              {isMaximized ? (
                <Minimize2 className="w-4 h-4" />
              ) : (
                <Maximize2 className="w-4 h-4" />
              )}
              {isMaximized ? "Restore" : "Maximize"}
            </button>

            {/* Close */}
            <button
              type="button"
              onClick={onClose}
              className="p-2 rounded-md hover:bg-gray-100"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="p-4 space-y-4 overflow-y-auto relative">
          {busyOverlay && (
            <div className="absolute inset-0 bg-white/60 backdrop-blur-[1px] flex items-center justify-center z-10">
              <div className="flex items-center gap-3 px-4 py-2 rounded-md bg-white shadow">
                <LoadingSpinner size="small" />
                <span className="text-sm text-gray-700">Working…</span>
              </div>
            </div>
          )}

          <Tabs active={tab} onChange={setTab} />

          {/* Active rejection / previous rejection context */}
          {(detail?.rejection_note || detail?.rejection_reason) &&
            normStatus(status) === "rejected" &&
            tab !== "summary" && (
            <div className="rounded-md border border-red-200 bg-red-50 p-3">
              <div className="flex items-start gap-2">
                <XCircle className="mt-0.5 h-4 w-4 shrink-0 text-red-600" />
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-red-800">
                    Inspection Rejected
                  </div>
                  <div className="mt-1 whitespace-pre-wrap text-sm text-red-700">
                    {detail.rejection_note || detail.rejection_reason}
                  </div>
                  {(detail.rejected_by_name || detail.rejected_by_role) && (
                    <div className="mt-2 text-xs text-red-600">
                      Rejected by {detail.rejected_by_name || "Unknown user"}
                      {detail.rejected_by_role
                        ? ` (${detail.rejected_by_role})`
                        : ""}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Actions (filtered by role, then enabled/disabled by status) */}
          <div className="flex flex-wrap items-center gap-2">
            {showAssignInspectionBtn && (
              <button
                type="button"
                disabled={!detail}
                onClick={() => onAssignInspection(detail)}
                className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md bg-orange-500 text-white text-xs hover:bg-orange-600 disabled:opacity-50"
              >
                <ClipboardCheck className="w-3.5 h-3.5" />
                <span>Assign Inspection</span>
              </button>
            )}

            {/* Submit (Inspector / PM only) */}
            {showSubmitBtn && (
              <LoadingButton
                loading={actionLoading.submit}
                loadingText="Submitting…"
                disabled={!detail || !canSubmit(status)}
                onClick={onSubmit}
                className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md bg-gray-900 text-white text-xs"
              >
                <span className="inline-flex items-center gap-1.5">
                  <Send className="w-3.5 h-3.5" />
                  <span>Submit</span>
                </span>
              </LoadingButton>
            )}

            {/* Approve / Reject (QA/QC only) */}
            {showApproveRejectBtn && (
              <>
                <LoadingButton
                  loading={actionLoading.approve}
                  loadingText="Approving…"
                  disabled={!detail || !canApprove(status, role)}
                  onClick={onApprove}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md bg-blue-600 text-white text-xs"
                >
                  <span className="inline-flex items-center gap-1.5">
                    <BadgeCheck className="w-3.5 h-3.5" />
                    <span>Approve</span>
                  </span>
                </LoadingButton>

                <LoadingButton
                  loading={actionLoading.reject}
                  loadingText="Rejecting…"
                  disabled={!detail || !canReject(status, role)}
                  onClick={onReject}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md bg-red-600 text-white text-xs"
                >
                  <span className="inline-flex items-center gap-1.5">
                    <XCircle className="w-3.5 h-3.5" />
                    <span>Reject</span>
                  </span>
                </LoadingButton>
              </>
            )}

            {/* Reopen (Inspector / PM only) */}
            {showReopenBtn && (
              <LoadingButton
                loading={actionLoading.reopen}
                loadingText="Reopening…"
                disabled={!detail || !canReopen(status, role)}
                onClick={onReopen}
                className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md bg-yellow-600 text-white text-xs"
              >
                <span className="inline-flex items-center gap-1.5">
                  <RefreshCcw className="w-3.5 h-3.5" />
                  <span>Reopen</span>
                </span>
              </LoadingButton>
            )}
          </div>

          {tab === "summary" && SummaryTab}
          {tab === "checklist" && ChecklistTab}
          {tab === "findings" && FindingsTab}

          <InspectionReportModal
            open={reportOpen}
            onClose={() => setReportOpen(false)}
            projectId={projectId}
            inspectionId={inspectionId}
            inspectionNumber={inspectionNumber}
          />
        </div>
      </div>
    </div>
  );
};

export default React.memo(InspectionDrawer);
